'use client';
import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Badge from '@/components/ui/Badge';
import type { Lesson } from '@/lib/mockData';

interface LessonCardProps {
  lesson: Lesson;
  onFavorite?: (id: string) => void;
  isFavorited?: boolean;
}

export default function LessonCard({ lesson, onFavorite, isFavorited }: LessonCardProps) {
  const gradeBadgeVariant =
    lesson.gradeBand === 'Elementary' ?'elementary'
      : lesson.gradeBand === 'Middle School' ?'middle' :'high';

  return (
    <div className="group bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden flex flex-col">
      {/* Thumbnail */}
      <div className="relative overflow-hidden h-44">
        <AppImage
          src={lesson.thumbnail}
          alt={`${lesson.title} — global citizenship lesson thumbnail showing ${lesson.topic.toLowerCase()} theme`}
          fill
          sizes="(max-width: 768px) 100vw, 33vw"
          className="object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        {lesson.isFeatured && (
          <div className="absolute top-3 left-3">
            <Badge variant="featured">Featured</Badge>
          </div>
        )}
        {lesson.isNew && (
          <div className="absolute top-3 left-3">
            <Badge variant="new">New</Badge>
          </div>
        )}
        {lesson.isFree && (
          <div className="absolute top-3 right-3">
            <Badge variant="free">Free</Badge>
          </div>
        )}
        <button
          onClick={() => onFavorite?.(lesson.id)}
          className={`absolute bottom-3 right-3 p-1.5 rounded-full transition-all duration-150 active:scale-90 ${
            isFavorited
              ? 'bg-primary-500 text-white' :'bg-white/80 text-stone-500 hover:bg-white hover:text-primary-500 opacity-0 group-hover:opacity-100'
          }`}
          aria-label={isFavorited ? 'Remove from favorites' : 'Save to favorites'}
        >
          <Icon name={isFavorited ? 'HeartIcon' : 'HeartIcon'} variant={isFavorited ? 'solid' : 'outline'} size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col p-4 gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={gradeBadgeVariant}>{lesson.gradeBand}</Badge>
          <Badge variant="topic">{lesson.topic}</Badge>
          {lesson.duration && <Badge variant="duration">{lesson.duration}</Badge>}
        </div>
        <h3 className="font-semibold text-stone-800 text-sm leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors duration-150">
          {lesson.title}
        </h3>
        <p className="text-xs text-stone-500 leading-relaxed line-clamp-2 flex-1">
          {lesson.description}
        </p>

        {/* SDG tags */}
        {lesson.sdgs.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {lesson.sdgs.slice(0, 3).map((sdg) => (
              <span
                key={`sdg-${lesson.id}-${sdg}`}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-xs font-medium"
              >
                SDG {sdg}
              </span>
            ))}
            {lesson.sdgs.length > 3 && (
              <span className="text-xs text-stone-400">+{lesson.sdgs.length - 3}</span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1 mt-auto">
          <Link
            href="/lesson-detail"
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white bg-primary-500 hover:bg-primary-600 active:scale-95 transition-all duration-150"
          >
            <Icon name="EyeIcon" size={13} />
            View Lesson
          </Link>
          {lesson.isFree ? (
            <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-accent-700 bg-accent-50 hover:bg-accent-100 active:scale-95 transition-all duration-150 border border-accent-200">
              <Icon name="ArrowDownTrayIcon" size={13} />
              Download
            </button>
          ) : (
            <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 active:scale-95 transition-all duration-150">
              <Icon name="ShoppingBagIcon" size={13} />
              Buy Unit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}